import type { ChangeEvent, InputHTMLAttributes, KeyboardEvent } from "react";
import React, { useMemo, useState } from "react";

import { Field, useAddonClasses } from "@flywire/headlessui-tailwindcss";
import { Listbox } from "@headlessui/react";
import classNames from "classnames";
import debounce from "lodash/debounce";

import { Exclamation } from "components/FieldList/Field/Exclamation/Exclamation";
import { Keys } from "constants/keys";
import { useInputAriaProps } from "hooks/useInputAriaProps/useInputAriaProps";
import { ReactComponent as Arrow } from "icons/arrow.svg";
import type { CountryForSelect } from "models";
import { joinPhoneNumberWithPrefix, splitPhoneNumber } from "utils/phone/phone";
import { useTranslations } from "utils/translations/useTranslations";

import "./PhoneInput.scss";

const WAIT_TIME = 250;

type PhoneInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "id" | "onChange" | "onBlur"
> & {
  id: string;
  countries: CountryForSelect[];
  error?: string | boolean;
  help?: string;
  label?: string;
  onBlur?: (id: string) => void;
  onChange?: (id: string, value?: string) => void;
};

const PhoneInput = ({
  countries,
  disabled = false,
  error,
  help,
  hidden,
  id,
  label,
  name,
  readOnly,
  required,
  type,
  value,
  onBlur,
  onChange,
  ...props
}: PhoneInputProps) => {
  const i18n = useTranslations();
  const dialingCodes = useMemo(
    () =>
      countries
        .filter(({ dialingCode }) => !!dialingCode)
        .map(({ dialingCode }) => dialingCode as string),
    [countries],
  );
  const phone = splitPhoneNumber(value as string, dialingCodes);
  const [prefix, setPrefix] = useState(phone.prefix);
  const [phoneNumber, setPhoneNumber] = useState(phone.phoneNumber);
  const addonClasses = useAddonClasses({
    position: "left",
    className: classNames(
      "bg-transparent cursor-pointer inline-block flex items-center justify-center gap-2 focus-visible:ring",
      prefix.length < 3 ? "w-24" : "w-28",
    ),
  });
  const isInvalid = error && typeof error === "string";

  const { inputAriaProps } = useInputAriaProps({
    error: error ?? false,
    label: label || "",
    name: id,
    disabled,
    hint: help,
    readOnly,
    required,
  });

  const onInputKeyDown = (evt: KeyboardEvent<HTMLInputElement>) => {
    const isControl = evt.ctrlKey || evt.metaKey;
    const isCopy = isControl && evt.key === Keys.C;
    const isCut = isControl && evt.key === Keys.X;
    const isNumber = /\d/.test(evt.key);
    const isPaste = isControl && evt.key === Keys.V;
    const allowedChars = [
      Keys.DELETE,
      Keys.ARROW_LEFT,
      Keys.NUMPAD_0,
      Keys.NUMPAD_1,
      Keys.NUMPAD_2,
      Keys.NUMPAD_3,
      Keys.NUMPAD_4,
      Keys.NUMPAD_5,
      Keys.NUMPAD_6,
      Keys.NUMPAD_7,
      Keys.NUMPAD_8,
      Keys.NUMPAD_9,
      Keys.ARROW_RIGHT,
      Keys.SUPR,
      Keys.TAB,
    ].includes(evt.key);

    if (!isNumber && !allowedChars && !isPaste && !isCopy && !isCut) {
      evt.preventDefault();
    }
  };

  const getA11yStatusMessage = (isOpen: boolean) => {
    const isClosed = !isOpen;

    if (isClosed) {
      return i18n.t("phoneInput.results.selected", { label: prefix });
    }

    const resultCount = `${countries.length}`;

    return resultCount === "1"
      ? i18n.t("phoneInput.results.one")
      : i18n.t("phoneInput.results.many", { resultCount });
  };

  const onPrefixChange = (value: string) => {
    setPrefix(value);
    _handleChange(joinPhoneNumberWithPrefix(value, phoneNumber));
  };

  const onInputChange = (ev: ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(ev.target.value || "");
    _handleChange(joinPhoneNumberWithPrefix(prefix, ev.target.value));
  };

  const _handleChange = (value: string) => {
    onChange?.(id, value);
  };

  return (
    <Field id={id}>
      <Field.Group as="div" onBlur={() => onBlur?.(id)} className="relative">
        <Listbox
          value={prefix}
          onChange={onPrefixChange}
          disabled={readOnly || disabled}
        >
          {({ open }) => (
            <>
              <Listbox.Button
                className={addonClasses}
                data-testid="phoneInputField-prefix"
                onBlur={() => onBlur?.(id)}
              >
                <span>+ {prefix}</span>
                <Arrow
                  className={classNames(
                    "transition-all",
                    open ? "rotate-180" : "rotate-0",
                  )}
                />
              </Listbox.Button>
              <Options
                countries={countries}
                className="absolute z-10 mt-14 max-h-60 w-full list-none overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-gray-500 focus-visible:outline-none"
              />
              <div
                role="status"
                aria-live="polite"
                aria-relevant="additions text"
                className="sr-only"
              >
                {getA11yStatusMessage(open)}
              </div>
            </>
          )}
        </Listbox>

        <div className="relative w-full">
          <Field.Input
            position="right"
            data-testid="phoneInputField-input"
            defaultValue={phoneNumber}
            id={id}
            name={name}
            readOnly={readOnly}
            disabled={disabled}
            type={type}
            hidden={hidden}
            pattern="[0-9]+"
            onChange={debounce(onInputChange, WAIT_TIME)}
            onKeyDown={onInputKeyDown}
            placeholder={label}
            required={required}
            {...props}
            {...inputAriaProps}
          />
          {isInvalid && (
            <div className="absolute top-3 ltr:right-3 rtl:left-3">
              <Exclamation className="h-4 w-4 text-red-600" />
            </div>
          )}
          <Field.Label id={`${id}-label`}>{label}</Field.Label>
        </div>
      </Field.Group>
      {help && (
        <Field.Hint className="mb-0" data-testid={`${id}-hint`}>
          {help}
        </Field.Hint>
      )}
      {isInvalid && (
        <Field.Error className="mb-0" data-testid={`${id}-error`}>
          {i18n.t(error)}
        </Field.Error>
      )}
    </Field>
  );
};

const Options = ({
  countries,
  className,
}: {
  countries: CountryForSelect[];
  className?: string;
}) => {
  return (
    <Listbox.Options className={className}>
      {countries
        .filter(({ dialingCode }) => !!dialingCode)
        .map((country, index) => (
          <Listbox.Option key={index} value={country.dialingCode}>
            {({ active }) => (
              <div
                className={classNames("PhoneInput-option", {
                  "is-active": active,
                })}
                data-testid={`dial_${country.dialingCode}`}
              >
                <span className="PhoneNumber-option-country">
                  {country.label}
                </span>
                <span className="PhoneNumber-option-dial">
                  +{country.dialingCode}
                </span>
              </div>
            )}
          </Listbox.Option>
        ))}
    </Listbox.Options>
  );
};

export { PhoneInput };
